import { useCallback } from 'react';
import { LocationData, InstalledHdaSwitch, InstalledCoreSwitch } from '../types';
import { HDA_SWITCH_TYPES, CORE_SWITCH_TYPES, POWER_GRIDS } from '../constants';
import { calculateLocationGrossPower } from '../utils/powerCalculations';

interface NetworkManagementProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: 'success' | 'warning' | 'info' | 'error', duration?: number) => void;
  gameTime: number;
}

export interface NetworkManagementHook {
  installHdaSwitch: (locationId: string, floorId: string, switchTypeId: string) => void;
  installCoreSwitch: (locationId: string, switchTypeId: string) => void;
}

export const useNetworkManagement = ({
  locations,
  setLocations,
  money,
  setMoney,
  triggerNotification,
  gameTime,
}: NetworkManagementProps): NetworkManagementHook => {

  const installHdaSwitch = useCallback((locationId: string, floorId: string, switchTypeId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found.', 'error'); return; }

    // MMR must exist before any aggregation switching
    if (!location.mmr?.built) {
      triggerNotification('Build the Meet-Me Room (MMR) first. Network equipment connects back to the MMR.', 'warning');
      return;
    }

    const floor = location.floors[floorId];
    if (!floor) { triggerNotification('Floor not found.', 'error'); return; }

    // Require at least one rack with a TOR switch on this floor
    const racksWithTOR = (floor.racks ?? []).filter(r => r.type !== null && r.torSwitch);
    if (racksWithTOR.length === 0) {
      triggerNotification('Install at least one rack with a TOR switch on this floor before adding an HDA switch.', 'warning');
      return;
    }

    const switchType = HDA_SWITCH_TYPES.find(t => t.id === switchTypeId);
    if (!switchType) { triggerNotification('Unknown switch type.', 'error'); return; }

    if (floor.hdaSwitch) {
      const existing = HDA_SWITCH_TYPES.find(t => t.id === floor.hdaSwitch!.typeId);
      if (existing && existing.bandwidthGbps >= switchType.bandwidthGbps) {
        triggerNotification('This floor already has an equal or higher-tier HDA switch.', 'warning');
        return;
      }
    }

    if (money < switchType.cost) {
      triggerNotification(`Not enough money. Need $${switchType.cost.toLocaleString()}.`, 'error');
      return;
    }

    // Deduct old HDA switch power if replacing
    const oldHdaType = floor.hdaSwitch
      ? HDA_SWITCH_TYPES.find(t => t.id === floor.hdaSwitch!.typeId)
      : null;
    const oldHdaPower = oldHdaType?.powerDrawKW ?? 0;
    const powerDelta = switchType.powerDrawKW - oldHdaPower;

    const installed: InstalledHdaSwitch = { typeId: switchTypeId, installedAt: gameTime };
    setMoney(prev => prev - switchType.cost);
    setLocations(prev => {
      const loc = prev[locationId];
      const updatedLoc: LocationData = {
        ...loc,
        usedPower: (loc.usedPower ?? 0) + powerDelta,
        floors: {
          ...loc.floors,
          [floorId]: { ...loc.floors[floorId], hdaSwitch: installed },
        },
      };
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;
      return { ...prev, [locationId]: updatedLoc };
    });
    triggerNotification(`${switchType.name} installed on ${floor.name}.`, 'success');
  }, [locations, money, setMoney, setLocations, triggerNotification, gameTime]);

  const installCoreSwitch = useCallback((locationId: string, switchTypeId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found.', 'error'); return; }

    // MMR must exist — the core switch is racked inside/adjacent to the MMR
    if (!location.mmr?.built) {
      triggerNotification('Build the Meet-Me Room (MMR) first. The Core Switch is racked there.', 'warning'); return;
    }

    const switchType = CORE_SWITCH_TYPES.find(t => t.id === switchTypeId);
    if (!switchType) { triggerNotification('Unknown switch type.', 'error'); return; }

    if (location.coreSwitch) {
      const existing = CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId);
      if (existing && existing.bandwidthGbps >= switchType.bandwidthGbps) {
        triggerNotification('This location already has an equal or higher-tier core switch.', 'warning');
        return;
      }
    }

    if (money < switchType.cost) {
      triggerNotification(`Not enough money. Need $${switchType.cost.toLocaleString()}.`, 'error');
      return;
    }

    // Deduct old core switch power if replacing
    const oldCoreType = location.coreSwitch
      ? CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId)
      : null;
    const oldCorePower = oldCoreType?.powerDrawKW ?? 0;
    const powerDelta = switchType.powerDrawKW - oldCorePower;

    const installed: InstalledCoreSwitch = { typeId: switchTypeId, installedAt: gameTime };
    setMoney(prev => prev - switchType.cost);
    setLocations(prev => {
      const loc = prev[locationId];
      const updatedLoc: LocationData = {
        ...loc,
        usedPower: (loc.usedPower ?? 0) + powerDelta,
        coreSwitch: installed,
      };
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;
      return { ...prev, [locationId]: updatedLoc };
    });
    triggerNotification(`${switchType.name} installed at ${location.name}.`, 'success');
  }, [locations, money, setMoney, setLocations, triggerNotification, gameTime]);

  return { installHdaSwitch, installCoreSwitch };
};
